/**
 * Click/tap selection via raycast. Left-click selects a selectable object,
 * right-click is reserved by main.ts for move orders; on touch, taps route
 * through selectAt() via the smart-tap logic in main.ts.
 */
import * as THREE from "three";

export class Selection {
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  selected: THREE.Object3D | null = null;
  /**
   * Box-selected group (touch select-mode drags, desktop shift/drag-box).
   * Holds own fighters; empty unless a box select is active. A single
   * select replaces the group and vice versa — they never coexist.
   */
  selectedMany: THREE.Object3D[] = [];
  /** instanceId of the last click when the hit was an InstancedMesh. */
  lastInstanceId: number | null = null;
  onSelect: ((obj: THREE.Object3D | null) => void) | null = null;
  /** When true, left-clicks pass through (e.g. while placing a building). */
  suspended = false;

  constructor(
    private readonly camera: THREE.Camera,
    private readonly dom: HTMLElement,
    private readonly selectables: THREE.Group,
    private readonly highlight: (obj: THREE.Object3D | null) => void,
  ) {
    dom.addEventListener("contextmenu", (e) => e.preventDefault());
    dom.addEventListener("mousedown", (e) => {
      if (e.button !== 0 || this.suspended) return;
      this.selectAt(e.clientX, e.clientY);
    });
  }

  /** Raycast-select whatever is under the given client coordinates. */
  selectAt(clientX: number, clientY: number): void {
    if (this.suspended) return;
    const rect = this.dom.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.selectables.children, true);
    let obj: THREE.Object3D | null = null;
    this.lastInstanceId = null;
    if (hits.length > 0) {
      obj = hits[0].object;
      this.lastInstanceId = hits[0].instanceId ?? null;
      // Walk up to the registered selectable root.
      while (obj && obj.parent !== this.selectables && obj.parent) {
        obj = obj.parent;
      }
    }
    this.setSelected(obj);
  }

  setSelected(obj: THREE.Object3D | null): void {
    if (this.selected === obj && this.selectedMany.length === 0) return;
    this.unhighlightAll();
    this.selected = obj;
    this.selectedMany = [];
    this.lastInstanceId = null;
    if (obj) this.highlight(obj);
    this.onSelect?.(obj);
  }

  /** Replace the selection with a box-selected group of objects. */
  setSelectedMany(objs: THREE.Object3D[]): void {
    this.unhighlightAll();
    this.selected = null;
    this.selectedMany = [...objs];
    this.lastInstanceId = null;
    for (const o of this.selectedMany) this.highlight(o);
    this.onSelect?.(this.selectedMany[0] ?? null);
  }

  /** Drop one object from the group (e.g. it died); clears highlight. */
  removeFromMany(obj: THREE.Object3D): void {
    const i = this.selectedMany.indexOf(obj);
    if (i < 0) return;
    this.highlight(obj); // toggle off
    this.selectedMany.splice(i, 1);
    this.onSelect?.(this.selectedMany[0] ?? null);
  }

  /** True when anything (single or group) is selected. */
  get hasSelection(): boolean {
    return this.selected !== null || this.selectedMany.length > 0;
  }

  /** Toggle the highlight off for everything currently selected. */
  private unhighlightAll(): void {
    if (this.selected) this.highlight(this.selected);
    for (const o of this.selectedMany) this.highlight(o);
  }
}
